import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';

import { Icon } from 'components';
import { CANCELLED_STATUSES } from 'components/List/ListItems';
import { TourItemContent, TourModalType } from '../types';
import TourEditContext from '../TourEditContext';
import TourListingContext from '../TourListingContext';
import MobileFriendlyTooltip from '../MobileFriendlyTooltip';

export interface ListItemActionProps {
  item: TourItemContent;
}

const ListItemAction: React.FC<ListItemActionProps> = ({
  item: { id, status, appointmentId },
  item,
}) => {
  const { removeItemFromTour } = useContext(TourEditContext);
  const {
    setModalType,
    setAppointmentId,
    setListingModalState,
    setSelectedTourItem,
    setListingId,
  } = useContext(TourListingContext);

  const removeItem = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      e.stopPropagation();
      removeItemFromTour(id);
    },
    [id, removeItemFromTour],
  );

  const viewAppointment = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      e.stopPropagation();
      setModalType(TourModalType.APPOINTMENT);
      setAppointmentId(appointmentId);
      setSelectedTourItem(item);
      setListingModalState(true);
      setListingId(item.listingId);
    },
    [
      setModalType,
      setAppointmentId,
      appointmentId,
      setSelectedTourItem,
      item,
      setListingModalState,
      setListingId,
    ],
  );

  const RemoveItem = useMemo(() => (
      <MobileFriendlyTooltip title="Remove">
        <StyledIcon
          onMouseOver={e => e.stopPropagation()}
          onClick={removeItem}
          $item="remove"
          data-cy="remove-stop-button"
        >
          <Icon width={16} type="x" color="black" offset={[0, 2]} />
        </StyledIcon>
      </MobileFriendlyTooltip>
    ), [removeItem]);

  const EditItem = useMemo(() => (
      <MobileFriendlyTooltip title="Edit">
        <StyledIcon data-cy="edit-showing-button" onClick={viewAppointment}>
          <Icon type="edit" width={16} offset={[0, 2]} color="black" />
        </StyledIcon>
      </MobileFriendlyTooltip>
    ), [viewAppointment]);

  const ViewItem = useMemo(() => (
      <MobileFriendlyTooltip title="View">
        <StyledIcon data-cy="view-listing-button" onClick={viewAppointment}>
          <Icon type="eyeFilled" width={16} offset={[0, 2]} color="black" />
        </StyledIcon>
      </MobileFriendlyTooltip>
    ), [viewAppointment]);

  if (CANCELLED_STATUSES.includes(status)) {
    return ViewItem;
  }

  if (appointmentId) {
    return EditItem;
  }

  return RemoveItem;
};

export default ListItemAction;

// NOTE: did not use Button from components, too many styles to override and we don't need the consisten UI for this case
const StyledIcon = styled.button<{
  $item?: string;
}>`
  border-radius: 50%;
  width: ${({ $item }) => ($item === 'remove' ? '24px' : '36px')};
  height: ${({ $item }) => ($item === 'remove' ? '24px' : '36px')};
  position: absolute;
  top: 10px;
  right: 0;
  &:hover {
    background-color: #f5f5f5;
  }

  z-index: 1; // used to make sure icon has higher priority than RouteListingOrStop
`;
